const {
	clearModalZipError,
	enableZipInputAutoFocusOnShown,
	getTrimmedModalPostalCodeOrShowError,
	submitManualZipAndReload,
} = require('public/js/lib/location-modal-update');
const {
	getItem,
	STORAGE_KEYS,
} = require('public/js/lib/local-storage');

const inStockAndInStockOnlyWarehouseTypes = [
	'StockLevel',
	'InStockMessagingWithWarehouseOnly',
];

const getInventoryMessage = (message) => {
	const displayStockLevel = inStockAndInStockOnlyWarehouseTypes.includes(message.inventoryMessageType);
	let inventoryMessage = (displayStockLevel ? message.inventoryMessage.inventory_message : message.inventoryMessage) || '';
	if (message.inventoryMessageType === 'OutOfStockMessagingOnly') {
		inventoryMessage = `<i class="fa fa-exclamation-triangle"></i> ${inventoryMessage}`;
	}

	return inventoryMessage;
};

const getShippingMessage = (message) => {
	const displayStockLevel = inStockAndInStockOnlyWarehouseTypes.includes(message.inventoryMessageType);
	return (displayStockLevel ? message.inventoryMessage.shipping_message : null) || '';
};

const getShippingMessageTitle = (message) => {
	const displayStockLevel = inStockAndInStockOnlyWarehouseTypes.includes(message.inventoryMessageType);
	return (displayStockLevel ? message.inventoryMessage.shipping_message_title : null) || '';
};

const getShippingEstimateMessage = (message) => {
	const displayStockLevel = inStockAndInStockOnlyWarehouseTypes.includes(message.inventoryMessageType);
	return (displayStockLevel ? message.inventoryMessage.shipping_estimate_message : null) || '';
};

const getInventoryMessageWarehouseId = (message) => {
	const displayStockLevel = inStockAndInStockOnlyWarehouseTypes.includes(message.inventoryMessageType);
	return (displayStockLevel ? message.inventoryMessage.warehouse_id : null) || '';
};

const getInventoryMessageCustomerId = (message) => {
	const displayStockLevel = inStockAndInStockOnlyWarehouseTypes.includes(message.inventoryMessageType);
	return (displayStockLevel ? message.inventoryMessage.warehouse_customer_id : null) || '';
};

const getPickupMessage = (message) => {
	const displayStockLevel = inStockAndInStockOnlyWarehouseTypes.includes(message.inventoryMessageType);
	return (displayStockLevel ? message.inventoryMessage.pickup_message : null) || '';
};

/**
 * Resolve the PDP ZIP modal for a delegated click (callout links or estimate link inside purchase module).
 * Prefers `data-pdp-zip-modal-id` on the callout + unique modal `id` (see product_purchase data.php); falls back
 * to `.find('.pdp-zip-code-modal')` when the attribute is missing.
 *
 * @param {JQuery.Event} evt
 * @returns {JQuery}
 */
const getPdpZipModalFromZipInteraction = (evt) => {
	const $fromCallout = rp$(evt.currentTarget).closest('.pdp-zip-callout');
	let modalId = '';
	if ($fromCallout.length > 0) {
		modalId = String($fromCallout.attr('data-pdp-zip-modal-id') ?? '');
	} else {
		const $module = rp$(evt.currentTarget).closest('.product-purchase-module');
		modalId = String($module.find('.pdp-zip-callout').first().attr('data-pdp-zip-modal-id') ?? '');
	}
	if (modalId !== '') {
		const $byId = rp$('#' + modalId);
		if ($byId.length > 0) {
			return $byId;
		}
	}
	const $purchaseModule = rp$(evt.currentTarget).closest('.product-purchase-module');

	return $purchaseModule.find('.pdp-zip-code-modal').first();
};

/**
 * "City, postal" for PDP callout from normalized `location` (same idea as server had for locationLabel).
 *
 * @param {{ postal_code?: string, postalCode?: string, city?: string }|null|undefined} location
 * @returns {string}
 */
const buildPdpCalloutCityPostalLabel = (location) => {
	if (!location || typeof location !== 'object') {
		return '';
	}
	const postal = String(location.postal_code ?? location.postalCode ?? '').trim();
	const city = String(location.city ?? '').trim();
	if (postal === '') {
		return '';
	}
	return city !== '' ? `${city}, ${postal}` : postal;
};

const handleZipChangeEvent = () => {
	const fillZipInput = ($modal, zipCodeRaw) => {
		const zipCode = typeof zipCodeRaw === 'string' ? zipCodeRaw.trim() : '';
		if (zipCode !== '') {
			$modal.find('.zip-input').val(zipCode);
		}
	};

	enableZipInputAutoFocusOnShown();

	rp$(document).off('click.pdpZip', '.shipping-estimate-postal-code');
	rp$(document).on('click.pdpZip', '.shipping-estimate-postal-code', (evt) => {
		evt.preventDefault();
		const $modal = getPdpZipModalFromZipInteraction(evt);
		clearModalZipError($modal);
		fillZipInput($modal, rp$(evt.currentTarget).text());
		if (typeof $modal.modal === 'function') {
			$modal.modal('show');
		}
	});

	rp$(document).off(
		'click.pdpZipEdit',
		'.pdp-zip-callout-enter, .pdp-zip-callout-destination'
	);
	rp$(document).on(
		'click.pdpZipEdit',
		'.pdp-zip-callout-enter, .pdp-zip-callout-destination',
		(evt) => {
			evt.preventDefault();
			const $purchaseModule = rp$(evt.currentTarget).closest('.product-purchase-module');
			const $modal = getPdpZipModalFromZipInteraction(evt);
			clearModalZipError($modal);
			const savedZipCodeRaw = getItem(STORAGE_KEYS.USER_ZIP_CODE);
			const savedZipCode = String(savedZipCodeRaw ?? '').trim();
			const visibleZipCode = $purchaseModule.find('.pdp-zip-callout-destination').first().text();
			const zipForFill = savedZipCode !== '' ? savedZipCode : visibleZipCode;
			fillZipInput($modal, zipForFill);
			if (typeof $modal.modal === 'function') {
				$modal.modal('show');
			}
		}
	);
};

/**
 * PDP ZIP modal submit. Identical contract to the header location modal: POST
 * `/ajax/update-location`, persist on success, reload so the page re-renders with the new
 * ZIP (inventory, shipping estimate, callout). Shared with the header via
 * {@link submitManualZipAndReload}.
 *
 * @param {string} zipCode  raw value from the modal input
 * @param {JQuery} [$modal] PDP zip modal; defaults to the first one on the page
 */
const handleZipChange = (zipCode, $modal) => {
	const $resolvedModal = ($modal?.length ?? 0) > 0
		? $modal
		: rp$('.product-purchase-module .pdp-zip-code-modal').first();
	const postalCode = getTrimmedModalPostalCodeOrShowError($resolvedModal, zipCode);
	if (postalCode === null) {
		return;
	}
	const $button = $resolvedModal.find('.enter-zip-button');
	submitManualZipAndReload({ postalCode }, $resolvedModal, $button);
};

module.exports = {
	buildPdpCalloutCityPostalLabel,
	getInventoryMessage,
	getInventoryMessageCustomerId,
	getInventoryMessageWarehouseId,
	getPickupMessage,
	getShippingEstimateMessage,
	getShippingMessage,
	getShippingMessageTitle,
	handleZipChange,
	handleZipChangeEvent,
};
